import React from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { formatSignOnTimestamp } from '../helpers/utils';

export default function TerminalHistoryTab({
  historyEntries = [],
  loading = false,
  terminalDisplayId = '',
  actionDefinitions = [],
}) {
  if (loading) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={32} sx={{ color: 'var(--reach-blue)' }} />
      </Box>
    );
  }

  if (historyEntries.length === 0) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ textAlign: 'center' }}
        >
          No terminal history available.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      variant='outlined'
      sx={{
        height: '100%',
        borderRadius: 2,
        borderColor: 'rgba(5, 5, 51, 0.08)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 1.5,
          pt: 1.5,
          pb: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: '1px solid rgba(5, 5, 51, 0.08)',
        }}
      >
        <Box>
          <Typography
            variant='subtitle2'
            sx={{
              fontFamily: 'BrightstarSans-Bold',
              color: 'var(--reach-blue)',
              lineHeight: 1.3,
            }}
          >
            Terminal History
          </Typography>
          <Typography variant='caption' color='text.secondary' display='block'>
            Last 5 actions for terminal {terminalDisplayId}
          </Typography>
        </Box>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ whiteSpace: 'nowrap', pt: 0.25 }}
        >
          Showing {historyEntries.length} most recent
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <List disablePadding dense>
          {historyEntries.map((entry, index) => {
            const action = actionDefinitions.find(
              def => def.id === entry.actionName,
            );
            const ActionIcon = action?.icon ?? PowerSettingsNewIcon;

            return (
              <Box key={entry.id}>
                <ListItem
                  alignItems='flex-start'
                  dense
                  sx={{ py: 0.75, px: 1.25, alignItems: 'flex-start' }}
                >
                  <ListItemAvatar sx={{ minWidth: 36, mt: 0.15 }}>
                    <Avatar
                      sx={{
                        width: 30,
                        height: 30,
                        bgcolor: action?.avatarBg ?? 'rgba(5, 5, 51, 0.08)',
                      }}
                    >
                      <ActionIcon
                        sx={{
                          fontSize: 16,
                          color: action?.iconColor ?? 'var(--reach-blue)',
                        }}
                      />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    disableTypography
                    primary={
                      <Typography
                        variant='body2'
                        sx={{
                          fontFamily: 'BrightstarSans-Bold',
                          color: 'var(--reach-blue)',
                          fontSize: '0.875rem',
                          lineHeight: 1.25,
                        }}
                      >
                        {action?.label ?? entry.actionName}
                      </Typography>
                    }
                    secondary={
                      <Stack spacing={0.15} sx={{ mt: 0.25 }}>
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ lineHeight: 1.3, fontSize: '0.75rem' }}
                        >
                          {formatSignOnTimestamp(entry.timestamp)} • {entry.agent}
                        </Typography>
                        <Typography
                          variant='caption'
                          sx={{
                            color: 'text.secondary',
                            fontStyle: 'italic',
                            lineHeight: 1.3,
                            fontSize: '0.75rem',
                            opacity: 0.9,
                          }}
                        >
                          {entry.status === 'Failed'
                            ? action?.error ?? 'Action failed'
                            : action?.description ?? 'Terminal action recorded'}
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItem>
                {index < historyEntries.length - 1 && (
                  <Divider component='li' sx={{ ml: 6.5, mr: 1.5 }} />
                )}
              </Box>
            );
          })}
        </List>
      </Box>
    </Paper>
  );
}
